import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid2,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { FinanceProgramForm } from '../../../../schema';
import { get, isEmpty } from 'lodash';
import { documentsByKey } from '../../../../../../constants/common';
import { SelectField } from '../../../../../../components/form';
import { useSelector } from 'react-redux';
import { FormSection } from '../../FormSection';

interface Props {
  isReadOnly: boolean;
}

interface RelatedUser {
  id: string;
  dynamicsContactId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber: string | null;
  mobilePhoneNumber: string | null;
  mugshot: string | null;
  availability: string | null;
  vendorContactRole: string;
  title: string | null;
  accountId: string;
  status: string;
}

export const SubmissionSettings = ({ isReadOnly }: Props) => {
  const { control, getValues, setValue, watch, resetField } = useFormContext<FinanceProgramForm>();
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([documentsByKey.credit_report.key]);

  const handleReset = useCallback(() => {
    resetField('program.first.submissionSettings');
    setSelectedDocuments([documentsByKey.credit_report.key]);
    setSelectedUser(null);
  }, [resetField]);
  const [selectedUser, setSelectedUser] = useState<RelatedUser | null>(null);

  const relatedUsers: RelatedUser[] = useSelector((state: any) => state.relatedUsers || []);

  useEffect(() => {
    let initialDocuments = [documentsByKey.credit_report.key];
    if (!isEmpty(getValues('program.first.submissionSettings.excludedDocuments'))) {
      initialDocuments = getValues('program.first.submissionSettings.excludedDocuments');
    }

    setSelectedDocuments(initialDocuments);
    setValue('program.first.submissionSettings.excludedDocuments', initialDocuments);
  }, []);

  useEffect(() => {
    const autoSubmitUserId = getValues('program.first.submissionSettings.autoSubmitUserProfileId');
    if (autoSubmitUserId) {
      const user = relatedUsers.find(user => user.id === autoSubmitUserId) || null;
      setSelectedUser(user);
    }
  }, [relatedUsers]);

  const handleChange = (event: any) => {
    setSelectedDocuments(event.target.value);
    setValue('program.first.submissionSettings.excludedDocuments', event.target.value);
  };

  const handleUserChange = (event: any, value: RelatedUser | null) => {
    setSelectedUser(value);
    if (value) {
      setValue('program.first.submissionSettings.autoSubmitUserProfileId', value.id);
    }
  };

  const getRoleName = (role: string) => {
    const roleMap: Record<string, string> = {
      sales_rep: 'Sales Rep',
      sales_mgr: 'Sales Manager',
      credit_mgr: 'Credit Manager',
      admin: 'Admin',
      support: 'Support',
    };
    return roleMap[role] || role;
  };

  // Sort users by role for grouping
  const sortedUsers = [...relatedUsers].sort((a, b) => {
    const roleA = getRoleName(a.vendorContactRole);
    const roleB = getRoleName(b.vendorContactRole);
    return roleA.localeCompare(roleB);
  });

  return (
    <FormSection title="Submission Settings" onReset={handleReset} isReadOnly={isReadOnly}>
      <Grid2 container spacing={3}>
        <Grid2 size={12}>
          <Typography component="span" variant="body1">
            Hide for <b>this</b> lender's Finance Program
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <FormControl fullWidth>
            <Select
              labelId="document-multiselect-label"
              multiple
              value={selectedDocuments}
              onChange={handleChange}
              renderValue={selected => selected.map(s => get(documentsByKey, `[${s}].text`)).join(', ')}
            >
              {Object.values(documentsByKey).map(doc => (
                <MenuItem key={doc.key} value={doc.key}>
                  <Checkbox checked={selectedDocuments.indexOf(doc.key as never) > -1} />
                  <ListItemText primary={doc.text} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid2>

        <Grid2 size={12}>
          <Typography component="span" variant="body1" sx={{ mt: 2 }}>
            Select User for Auto-Submit Role
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Autocomplete
            value={selectedUser}
            onChange={handleUserChange}
            options={sortedUsers}
            groupBy={option => getRoleName(option.vendorContactRole)}
            getOptionLabel={option => `${option.fullName} (${getRoleName(option.vendorContactRole)})`}
            disabled={isReadOnly}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => <TextField {...params} label="Auto-Submit Role" placeholder="Search users..." />}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{option.fullName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText="No users available"
          />
        </Grid2>
      </Grid2>
    </FormSection>
  );
};
